import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  useTheme,
  InputLabel,
  Typography,
  FilledInput,
} from "@material-ui/core";

import { useDialog } from "../../../hooks/useDialog";
import { RiderContext } from "../../../context";
import {
  ACCOUNT_STATUS,
  BANS_AND_SUSPENSION_REASON,
  HIGHEST_COMPLETED_JUMIO_LEVEL,
} from "../../../constants";
import { FilledTextField } from "../../../components/FormElements";
import { LoadingOverlay } from "../../../components/Navigation/LoadingOverlay";
import Permissions from "../../../permissions";
import { PermissionedDisplay } from "../../../components/Layout";
import { getCookies, scopesCookieKey } from "../../../API/cookies";
import { useTestIds } from "../../../hooks/useTestIds";

const useStyles = makeStyles(() => ({
  formControl: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  datePicker: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginTop: "0px",
  },
  formTitle: {
    marginTop: "-10px",
  },
  dropDown: {
    width: "50%",
    marginRight: "8px",
  },
  lengthField: {
    width: "50%",
    marginLeft: "8px",
  },
  button: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 5,
    margin: "0 16px 16px 0",
    width: "calc(25% - 24px)",
  },
  textField: {
    width: "100%",
    marginRight: "8px",
  },
}));

const ManageAccount = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles(theme);
  const testIds = useTestIds("rider-profile-manage-account-modal");
  const {
    accountStatus,
    setAccountStatus,
    activeRider,
    banOrSuspendAccount,
    softDeleteAccount,
    activateAccount,
    updateRiderInformation,
    setReasonForBanAndSuspension,
  } = useContext(RiderContext);

  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const [statusSelection, setStatusSelection] = useState(ACCOUNT_STATUS.Active);
  const [bansAndSuspensionReason, setBansAndSuspensionReason] = useState(
    BANS_AND_SUSPENSION_REASON.parking_violation,
  );

  const [suspensionLength, setSuspensionLength] = useState("");
  const [DOB, setDOB] = useState("");
  const [hideAgeVerification, setHideAgeVerification] = useState(true);
  const [notesInput, setNotesInput] = useState("");

  const [openDeleteDialog, deleteDialog] = useDialog({
    title: t("Riders.Account.DeleteDialogTitle"),
    body: t("Riders.Account.DeleteDialogBody"),
  });

  const { riderId } = useParams();
  const history = useHistory();

  useEffect(() => {
    const loadAccountStatus = async () => {
      await setAccountStatus(activeRider.suspensions);
    };
    if (activeRider) {
      loadAccountStatus();
      setStatusSelection(accountStatus);
    }
  }, [activeRider]);

  useEffect(() => {
    let mostRecentReason = "";
    let mostRecentNote = "";
    if (activeRider?.suspensions?.length > 0) {
      mostRecentReason = activeRider.suspensions.at(-1).reason ?? "";
      mostRecentNote = activeRider.suspensions.at(-1).notes ?? "";
    }

    const getReasonForBanAndSuspension = () => {
      setReasonForBanAndSuspension(mostRecentReason, mostRecentNote);
    };
    if (activeRider) {
      getReasonForBanAndSuspension();
      setBansAndSuspensionReason(mostRecentReason);
      setNotesInput(mostRecentNote);
    }
  }, [activeRider, accountStatus]);

  useEffect(() => {
    if (accountStatus === ACCOUNT_STATUS.Active) {
      setBansAndSuspensionReason("");
      setNotesInput("");
    }
  }, [accountStatus]);

  useEffect(() => {
    const riderJumioLevel = activeRider?.highest_completed_jumio_level;
    if (riderJumioLevel === "none") {
      setHideAgeVerification(false);
    }
  }, [activeRider]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setLoading(true);

    const status = statusSelection.split(" ")[0];
    const riderData = {
      date_of_birth: DOB,
      highest_completed_jumio_level: HIGHEST_COMPLETED_JUMIO_LEVEL,
      manual_dob_check: true,
    };

    try {
      if (status === ACCOUNT_STATUS.Active && DOB) {
        await updateRiderInformation(riderId, riderData);
      } else if (status === ACCOUNT_STATUS.Suspended) {
        await banOrSuspendAccount(riderId, suspensionLength, bansAndSuspensionReason, notesInput);
      } else if (status === ACCOUNT_STATUS.Banned) {
        await banOrSuspendAccount(riderId, null, bansAndSuspensionReason, notesInput);
      } else if (status === ACCOUNT_STATUS.Deleted) {
        tryDeleteAccount();
      } else if (status === ACCOUNT_STATUS.Active && activeRider.suspensions.length) {
        await activateAccount(activeRider);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);

    if (status !== ACCOUNT_STATUS.Deleted) {
      handleClose();
    }
  };

  const tryDeleteAccount = () => {
    openDeleteDialog(() => async () => {
      await softDeleteAccount(activeRider);
      handleClose();
    });
  };

  const validSuspensionInput = (value) => {
    if (value === "") return true;
    if (!value.match("^[0-9]+$")) return false;
    return value > 0 && value <= 100;
  };

  const onChangeSuspensionLength = (e) => {
    const { value } = e.target;
    if (validSuspensionInput(value)) {
      setSuspensionLength(value);
    }
  };

  const handleDateInput = (e) => {
    setDOB(e.target.value);
  };

  const disableSaveBtn = () => {
    const { Suspended } = ACCOUNT_STATUS;
    const dropDownSelection = statusSelection?.split(" ")[0].trim();
    const currentStatus = accountStatus?.split(" ")[0].trim();

    return (
      (dropDownSelection === Suspended && !suspensionLength) ||
      (dropDownSelection === currentStatus && !DOB)
    );
  };

  const scope = getCookies(scopesCookieKey);
  const tokenScopes = scope ? scope.split(" ") : [];

  let statuses = Object.keys(ACCOUNT_STATUS);
  const reasonsList = Object.keys(BANS_AND_SUSPENSION_REASON);

  const showReasonAndNotesFields = () =>
    statusSelection?.split(" ")[0].trim() === ACCOUNT_STATUS.Suspended ||
    statusSelection?.split(" ")[0].trim() === ACCOUNT_STATUS.Banned;

  if (!tokenScopes.includes(Permissions.deleteRidersAdmin)) {
    statuses = statuses.filter((status) => status !== "Deleted");
  }

  if (loading) return <LoadingOverlay />;

  return (
    <Grid>
      <Dialog
        data-testid={testIds.wrapper}
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle data-testid={testIds.title}>{t("Riders.Account.ManageAccount")}</DialogTitle>
        <DialogContent data-testid={testIds.content}>
          <Grid container justify="space-between">
            <Typography data-testid={testIds.subtitle} className={classes.formTitle}>
              {t("Riders.Account.AccountStatus.CurrentStatus")}
            </Typography>
            <FormControl
              className={classes.formControl}
              variant="filled"
              margin="normal"
              disabled={accountStatus?.split(" ")[0] === "Deleted"}
            >
              <InputLabel data-testid={testIds.selectStatusLabel}>
                {t("Riders.Account.AccountStatus.Status")}
              </InputLabel>
              <Select
                data-testid={testIds.selectStatusInput}
                value={statusSelection?.split(" ")[0]}
                onChange={(e) => setStatusSelection(e.target.value)}
                className={classes.dropDown}
                defaultValue=""
              >
                {statuses.map((status, idx) => (
                  <MenuItem
                    data-testid={`${testIds.selectStatusOption}-${idx}`}
                    key={status}
                    value={status}
                  >
                    {t(`Riders.Account.AccountStatus.${status}`)}
                  </MenuItem>
                ))}
              </Select>
              {statusSelection?.split(" ")[0] === "Suspended" && (
                <div className={classes.lengthField}>
                  <FilledTextField
                    label={t("Riders.Account.AccountStatus.Length")}
                    value={suspensionLength}
                    placeholder={t("Riders.Account.AccountStatus.Days")}
                    onChange={(e) => onChangeSuspensionLength(e)}
                    type="text"
                    style={{ margin: 0 }}
                    testIds={{
                      input: testIds.suspensionLengthInput,
                    }}
                  />
                </div>
              )}
            </FormControl>
          </Grid>
        </DialogContent>
        {showReasonAndNotesFields() && (
          <div>
            <DialogContent>
              <Grid container justify="space-between">
                <Typography data-testid={testIds.suspensionSubtitle} className={classes.formTitle}>
                  {t("Riders.Account.AccountStatus.SuspensionBanReason")}
                </Typography>
                <FormControl
                  className={classes.formControl}
                  variant="filled"
                  margin="normal"
                  disabled={accountStatus?.split(" ")[0] === "Deleted"}
                >
                  <InputLabel data-testid={testIds.selectReasonLabel}>
                    {t("Riders.Account.AccountStatus.Reason")}
                  </InputLabel>
                  <Select
                    data-testid={testIds.selectReasonInput}
                    value={bansAndSuspensionReason}
                    onChange={(e) => setBansAndSuspensionReason(e.target.value)}
                    className={classes.dropDown}
                  >
                    {reasonsList.map((reason, idx) => (
                      <MenuItem
                        data-testid={`${testIds.selectReasonOption}-${idx}`}
                        key={reason}
                        value={reason}
                      >
                        {t(`Riders.Account.AccountStatus.${reason}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </DialogContent>
            <DialogContent>
              <Grid container justify="space-between">
                <FormControl
                  className={classes.formControl}
                  variant="filled"
                  margin="normal"
                  disabled={accountStatus?.split(" ")[0] === "Deleted"}
                >
                  <InputLabel data-testid={testIds.notesLabel}>
                    {t("Riders.Account.AccountStatus.Notes")}
                  </InputLabel>
                  <FilledInput
                    data-testid={testIds.notesField}
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                    multiline
                    rows={3}
                    className={classes.textField}
                    inputProps={{ maxLength: 256 }}
                  ></FilledInput>
                </FormControl>
              </Grid>
            </DialogContent>
          </div>
        )}
        {!hideAgeVerification && (
          // temporarily hiding this from BPO Admins
          <PermissionedDisplay permission={Permissions.deleteRidersAdmin}>
            <DialogContent>
              <Grid container justify="space-between" item xs={6} md={6}>
                <Typography
                  data-testid={testIds.ageVerificationSubtitle}
                  className={classes.formTitle}
                >
                  {t("Riders.AgeVerificationDetails.AgeVerification")}
                </Typography>
                <FormControl className={classes.datePicker} variant="filled" margin="normal">
                  <FilledTextField
                    type="date"
                    label={t("Riders.AgeVerificationDetails.BirthDate")}
                    underlineColor={theme.palette.text.lightgrey}
                    InputLabelProps={{ shrink: true }}
                    value={DOB}
                    onChange={(e) => handleDateInput(e)}
                    testIds={{
                      input: testIds.birthDateField,
                    }}
                  />
                </FormControl>
              </Grid>
            </DialogContent>
          </PermissionedDisplay>
        )}
        <DialogActions>
          <Button
            data-testid={testIds.cancelButton}
            className={classes.button}
            onClick={handleClose}
          >
            {t("Riders.Wallet.Cancel")}
          </Button>
          <Button
            data-testid={testIds.saveButton}
            className={classes.button}
            onClick={handleSubmit}
            disabled={disableSaveBtn()}
          >
            {t("Riders.Wallet.Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {deleteDialog}
    </Grid>
  );
};

export default ManageAccount;
